import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography,
} from "@mui/material"
import { CloseOutlined } from "@mui-symbols-material/w300"
import { type FC, useCallback, useState } from "react"

import { ScannedGoodsReceiptPalletCard } from "./ScannedGoodsReceiptPalletCard"

import { Button } from "@/components/common/Button"
import type { GoodsReceiptPallet } from "@/graphql/codegen/graphql"
import { useAppView } from "@/hooks/useAppView"
import { useGetPalletByReferenceId } from "@/screens/Tablet/hooks/useGetPalletByReferenceId"
import { useScrollToBottom } from "@/screens/Tablet/hooks/useScrollToBottom"
import { useUpdateGoodsReceiptPallet } from "@/screens/Tablet/hooks/useUpdateGoodsReceiptPallet"
import { type DestinationOverride } from "@/screens/WarehouseReceiving/components/GoodsReceiptPalletsTable"
import { useGoodsReceiptPalletLabelPrinting } from "@/screens/WarehouseReceiving/hooks"

interface PalletUIState {
  repeatedScan: boolean
  scanCount: number
}

interface StoreProductModalProps {
  onClose: () => void
  onFinish: () => void
  scannedPallets: GoodsReceiptPallet[]
  palletUIState: Map<string, PalletUIState>
  onPalletUpdate: (pallet: GoodsReceiptPallet) => void
}

export const StoreProductModal: FC<StoreProductModalProps> = ({
  onClose,
  onFinish,
  scannedPallets,
  palletUIState,
  onPalletUpdate,
}: StoreProductModalProps) => {
  const { isTabletView } = useAppView()
  const { ref: contentRef } = useScrollToBottom({
    trigger: scannedPallets.length,
    enabled: true,
  })
  const { updatePallet } = useUpdateGoodsReceiptPallet()
  const { fetchPallet } = useGetPalletByReferenceId()

  const { printPalletLabel } = useGoodsReceiptPalletLabelPrinting()

  const [destinationOverrides, setDestinationOverrides] = useState<Map<string, DestinationOverride>>(new Map())

  const handleDestinationUpdate = useCallback(
    async (pallet: GoodsReceiptPallet, override: DestinationOverride) => {
      const updateResult = await updatePallet({
        goodsReceiptPalletId: pallet.goodsReceiptPalletId,
        destinationWarehouseShelvingId: override.warehouseShelvingId,
      })

      if (updateResult.error) {
        return
      }

      setDestinationOverrides((prevOverrides) => new Map(prevOverrides).set(pallet.goodsReceiptPalletId, override))

      const updatedPallet = await fetchPallet(pallet.referenceId)
      if (!updatedPallet) {
        return
      }

      onPalletUpdate(updatedPallet)
    },
    [updatePallet, fetchPallet, onPalletUpdate]
  )

  const handleQrPrint = async ({ referenceId }: GoodsReceiptPallet) => {
    await printPalletLabel(referenceId)
  }

  return (
    <Dialog open onClose={onClose} fullScreen={isTabletView} data-testid='store-product-modal' maxWidth='md' fullWidth>
      <DialogTitle className='flex justify-center pl-10 font-normal'>Store Products</DialogTitle>
      <IconButton aria-label='close' onClick={onClose} className='absolute right-4 top-3 text-primary'>
        <CloseOutlined />
      </IconButton>
      <Divider />
      <DialogContent ref={contentRef} className='bg-gray-100'>
        <Typography variant='body2' color='text.secondary' className='mb-6 flex justify-center'>
          Store each product in its assigned location and then click Finish Storing Products.
        </Typography>

        <Stack spacing={2}>
          {scannedPallets.map((pallet) => {
            const uiState = palletUIState.get(String(pallet.referenceId))
            return (
              <ScannedGoodsReceiptPalletCard
                key={pallet.referenceId}
                pallet={pallet}
                repeatedScan={uiState?.repeatedScan}
                scanCount={uiState?.scanCount}
                pallets={scannedPallets}
                destinationOverrides={destinationOverrides}
                onDestinationOverride={handleDestinationUpdate}
                onQrPrint={() => handleQrPrint(pallet)}
              />
            )
          })}
        </Stack>
      </DialogContent>

      <DialogActions className='gap-2 p-4'>
        <Button appearance='outlined' onClick={onClose} data-testid='cancel-button' className='flex-1'>
          Cancel
        </Button>
        <Button appearance='contained' onClick={onFinish} data-testid='finish-storing-button' className='flex-1'>
          Finish Storing Products
        </Button>
      </DialogActions>
    </Dialog>
  )
}
